import { useMemo } from 'react';
import { abbreviate } from '@chainflip/utils/string';
import BlockIcon from '@/shared/assets/svg/block.svg';
import ExtrinsicFailed from '@/shared/assets/svg/extrinsic-failed.svg';
import ExtrinsicSuccess from '@/shared/assets/svg/extrinsic-success.svg';
import { Link } from '@/shared/components/atoms/Link';
import AgeWithTooltip from '@/shared/components/molecules/AgeWithTooltip';
import CopyButton from '@/shared/components/molecules/CopyButton';
import { FlipLabel } from '@/shared/components/molecules/FlipLabel';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import {
  getExtrinsicsByAccountQuery,
  type CastExtrinsicFragment,
  extrinsicCastFields,
} from '@/shared/graphql/explorer/extrinsics';
import useRemotePagination from '@/shared/hooks/useRemotePagination';
import { buildChainflipExplorerLink } from '@/shared/utils/chainflip';
import FlipAmount from '@/shared/utils/FlipAmount';
import { formatWithCommas } from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';

const ExtrinsicRow = ({ extrinsic }: { extrinsic: CastExtrinsicFragment }) => (
  <tr className="text-14">
    <td className="text-12">
      {extrinsic?.account ? (
        <div className="flex flex-col gap-y-2">
          {extrinsic.account.alias}
          <div className="flex items-center gap-x-1">
            <Link
              href={buildChainflipExplorerLink(extrinsic.account.idSs58, 'validators')!}
              target={process.env.NEXT_PUBLIC_EXPLORER_URL ? '_blank' : undefined}
              rel="noreferrer"
              underline
              noPropagate
              color="light"
              className="whitespace-nowrap"
            >
              {abbreviate(extrinsic.account.idSs58, 4)}
            </Link>
            <CopyButton textToCopy={extrinsic.account.idSs58} />
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center">-</div>
      )}
    </td>
    <td>
      <Link
        href={buildChainflipExplorerLink(extrinsic.block.id.toString(), 'blocks')}
        target="_blank"
        rel="noreferrer"
        noPropagate
      >
        <span className="flex items-center">
          <BlockIcon className="mr-[5px] text-cf-green-1" />
          <span className="cursor-pointer underline">{formatWithCommas(extrinsic.block.id)}</span>
        </span>
      </Link>
    </td>
    <td>
      <Link
        href={buildChainflipExplorerLink(
          `${extrinsic.blockId}-${extrinsic.indexInBlock}`,
          'extrinsics',
        )}
        target="_blank"
        rel="noreferrer"
        className="cursor-pointer truncate underline"
        noPropagate
      >
        <span className="text-white">{abbreviate(extrinsic.hash, 6)}</span>
      </Link>
    </td>
    <td className="flex h-full items-center space-x-1">
      {extrinsic.success ? <ExtrinsicSuccess /> : <ExtrinsicFailed />}
      <span>{extrinsic.metadata.name}</span>
    </td>
    <td>
      <FlipLabel amount={extrinsic.fee ?? TokenAmount.ZERO} />
    </td>
    <td className="whitespace-nowrap text-cf-light-2">
      <AgeWithTooltip timestamp={extrinsic.block.timestamp} />
    </td>
  </tr>
);

const ExtrinsicsTable = ({ accountIds }: { accountIds: string[] }): React.JSX.Element => {
  const { data, paginationControls, isLoading, error } = useRemotePagination(
    getExtrinsicsByAccountQuery,
    {
      variables: { accountIds },
      enabled: accountIds.length > 0,
      useCursor: true,
    },
  );

  const optimizedExtrinsicRows = useMemo(
    () =>
      FlipAmount.castObject(data, extrinsicCastFields)?.extrinsics?.edges.map(({ node }) => (
        <ExtrinsicRow key={node.id} extrinsic={node} />
      )) ?? [],
    [data],
  );

  return (
    <TableV2
      columns={['Validator ID', 'Block', 'Extrinsic Hash', 'Extrinsic', 'Fee', 'Age']}
      rows={optimizedExtrinsicRows}
      isLoading={isLoading}
      paginationControls={paginationControls}
      emptyRowsContent={error !== null ? 'No recent transactions found' : undefined}
    />
  );
};

export default ExtrinsicsTable;
